import React from 'react';
import { Box, Checkbox, Typography, useMediaQuery } from '@mui/material';
import { SpinHistory } from '../../types';
import { colors } from '../../theme/themeConfig';

interface DiscountItemProps {
  discount: SpinHistory;
  isActive: boolean;
  isDisabled: boolean;
  onToggle: (discountId: string, isActive: boolean) => void;
}

/**
 * Simple discount item with checkbox for selection
 * Has three states: normal, active, disabled
 */
const DiscountItem: React.FC<DiscountItemProps> = ({
  discount,
  isActive,
  isDisabled,
  onToggle
}) => {
  const isMobile = useMediaQuery('(max-width: 375px)');
  
  // Get discount amount and minimum purchase from the reward
  const discountAmount = discount.reward.rewardAmount;
  const minPurchase = discount.reward.minPurchase;
  
  // Handle checkbox change
  const handleChange = () => {
    if (!isDisabled) {
      onToggle(discount.id, !isActive);
    }
  };
  
  return (
    <Box 
      sx={{ 
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: '8px',
        backgroundColor: isActive 
          ? 'rgba(142, 36, 170, 0.1)' // Light purple tint for active (primary color with low opacity)
          : isDisabled 
            ? 'rgba(117, 117, 117, 0.1)' // Light gray tint for disabled
            : 'rgba(255, 255, 255, 0.03)', // Very subtle highlight for normal
        border: `1px solid ${
          isActive 
            ? colors.primary.main 
            : isDisabled 
              ? 'rgba(117, 117, 117, 0.3)' // Subtle gray border for disabled
              : 'rgba(255, 255, 255, 0.1)' // Subtle border for normal
        }`,
        mb: 1,
        opacity: isDisabled ? 0.7 : 1,
        transition: 'all 0.2s ease',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
        '&:after': isActive ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          backgroundColor: colors.primary.main,
        } : {},
      }}
    >
      <Box sx={{ ml: 1 }}>
        <Typography 
          variant={isMobile ? "body2" : "body1"} 
          sx={{ 
            fontWeight: 500,
            color: isDisabled ? colors.text.disabled : colors.text.primary
          }}
        >
          Скидка {discountAmount}₽
        </Typography>
        
        <Typography 
          variant="caption" 
          sx={{ 
            color: isDisabled ? colors.text.disabled : colors.text.secondary,
            display: 'block'
          }}
        >
          Мин. сумма заказа: {minPurchase}₽
        </Typography>
      </Box>

      <Checkbox 
        checked={isActive}
        onChange={handleChange}
        disabled={isDisabled}
        sx={{ 
          color: isDisabled ? colors.text.disabled : colors.primary.light,
          '&.Mui-checked': {
            color: colors.primary.main,
          },
          '&:hover': {
            backgroundColor: isDisabled ? 'transparent' : 'rgba(142, 36, 170, 0.08)',
          },
        }}
      />
    </Box>
  );
};

export default DiscountItem;
